import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs } from '../../components';

import ImageBanner from '../../components/common/image-banner';
import BeforeAfterSlider from '../../components/template/fno-overview/before-after-slider';
import AnchorLinks from '../../components/common/anchor-links';
import TabSlider from '../../components/common/tabs-slider';
import DynamicComponent from '../../components/common/dynamic-component';
import LocalNavTop from '../../components/common/local-nav-top';
import HeroSimple from '../../components/common/hero-simple';

import styles from './index.module.scss';
import { objectHasValues } from '../../components/utilities/validations';

function FNOOverview(props) {
	const pageData = props.data.contentstackFnoOverview;

	return (
		<Layout
			id='template-FNOOverview'
			pageTitle={pageData.seo?.title}
		>
			{pageData.local_nav?.nav_links?.[0] && (
				<LocalNavTop
					navTitle={pageData.local_nav.heading}
					linksArray={pageData.local_nav.nav_links.map((link) => link.cta[0])}
				/>
			)}

			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
				/>
			)}

			{pageData.hero && <HeroSimple data={pageData.hero} />}

			{pageData.top_content && (
				<div className={styles.windowOption_Container}>
					<Container width={1800}>
						{pageData.top_content.heading && <h2 className={`${styles.windowOption_Heading} s2-h2__alt`}>{pageData.top_content.heading}</h2>}
						{pageData.top_content.description && (
							<div
								className={styles.windowOption_Content}
								dangerouslySetInnerHTML={{
									__html: pageData.top_content.description,
								}}
							/>
						)}
						{pageData.top_content.cta?.[0] && <AnchorLinks data={pageData.top_content.cta} />}
					</Container>
				</div>
			)}

			{pageData.window_options && <TabSlider data={pageData.window_options} />}

			{pageData.inspiration?.dynamic?.[0] && <DynamicComponent data={pageData.inspiration} />}

			{pageData.styling_your_window && <TabSlider data={pageData.styling_your_window} />}

			{pageData.tips_and_advice_tag_items?.dynamic?.[0] && <DynamicComponent data={pageData.tips_and_advice_tag_items} />}

			{pageData.enhancements && <TabSlider data={pageData.enhancements} />}

			{pageData.vision && (
				<div className={styles.vision_Container}>
					<Container width={1800}>
						{pageData.vision.heading && <h2 className={`${styles.vision_Heading} s2-h2__alt`}>{pageData.vision.heading}</h2>}
						{pageData.vision.description && (
							<div
								className={styles.vision_Content}
								dangerouslySetInnerHTML={{
									__html: pageData.vision.description,
								}}
							/>
						)}
					</Container>
				</div>
			)}

			{pageData.before_after && <BeforeAfterSlider data={pageData.before_after} />}

			{objectHasValues(pageData.image_aside) && <ImageBanner data={pageData.image_aside} />}
		</Layout>
	);
}

FNOOverview.propTypes = {
	data: PropTypes.object,
	location: PropTypes.object,
};

export default FNOOverview;

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => <SEO {...data.contentstackFnoOverview.seo} />;

export const query = graphql`
	query ($uid: String) {
		contentstackFnoOverview(uid: { eq: $uid }) {
			url
			before_after {
				image_pairs {
					...Image
				}
			}
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			local_nav {
				...LocalNav
			}
			hero {
				image {
					...Image
					image {
						gatsbyImageData(width: 1500, quality: 90)
					}
				}
				body
			}
			top_content {
				heading
				description
				cta {
					...CTA
				}
			}
			window_options {
				anchor
				heading
				subheading
				description
				tabData: window_options {
					tab_name: window_options_tabs
					heading
					description
					mask
					ctas {
						...CTA
					}
					image {
						...Image
						image {
							gatsbyImageData(height: 600, quality: 90)
						}
					}
				}
			}
			inspiration {
				heading
				dynamic {
					...Dynamic
				}
			}
			styling_your_window {
				anchor
				heading
				subheading
				description
				tabData: fno_tabs {
					tab_name
					heading
					description
					mask
					ctas {
						...CTA
					}
					image {
						...Image
						image {
							gatsbyImageData(height: 600, quality: 90)
						}
					}
				}
			}
			tips_and_advice_tag_items {
				heading
				dynamic {
					...Dynamic
				}
			}
			enhancements {
				anchor
				heading
				subheading
				description
				tabData: enhancement_tabs {
					heading
					tab_name
					description
					mask
					ctas {
						...CTA
					}
					image {
						...Image
						image {
							gatsbyImageData(height: 600, quality: 90)
						}
					}
				}
			}
			vision {
				heading
				description
			}
			before_after {
				image_pairs {
					...Image
				}
				numbered_content {
					heading
					description
				}
				ctas {
					title
					...CTA
				}
			}
			image_aside {
				...ImageAside
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
